import { useTranslation } from "@/i18n/client";
import { Box, Stack } from "@mantine/core";
import { Modal } from "@/components/Modal";
import { ContextModalProps } from "@mantine/modals";
import Text from "@/components/Text";
import { Process, Segment } from "@/types/contract";
import { InfoEmail, VRBEmail, B2BEmail } from "@/constants/common";
import { useForm } from "@mantine/form";
import { useContext, useEffect } from "react";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import { useUpdateMutation } from "@/hooks/useUpdateMutation";
import { createExecutivePeopleInitialValues } from "@/screens/Customer/ExecutivePeople/helpers";
import { Person } from "@/screens/common/Person";
import { ExecutiveRole } from "@/screens/common/ExecutiveRole";
import ButtonsGroup from "@/components/ButtonsGroup";
import {
  DEFAULT_EXECUTIVE_PERSON,
  executivePersonMainReferenceType,
  executivePersonOtherReferenceType,
  executiveRoleId,
} from "@/constants/executivePerson";
import { ExecutivePersonSelect } from "@/screens/common/ExecutivePerson";
import { BirthDate } from "@/screens/common/BirthDate";
import { FileTypeCode } from "@/constants/attachment";
import { getAttachmentName } from "@/utils/attachments";
import { FileInput } from "@/screens/common/FileInput";
import { capitalizeLetters } from "@/utils/string";
import { validationsExecutivePeople } from "@/screens/Customer/validations/executivePeople";
import { ExecutivePeopleFormValues } from "@/screens/Customer/ExecutivePeople/form";
import { useContractState } from "@/hooks/useContractState";
import { InputTooltip } from "@/components/InputTooltip";
import { Trans } from "react-i18next";
import Link from "@/components/Link";
import Input from "@/components/Input";
import MobilePhone from "@/screens/common/MobilePhone";
import { sessionStorageKeys } from "@/constants/common";
import Form from "@/screens/common/Form";
import { useIsContractRequest } from "@/hooks/useIsContractRequest";

const rightButtonProps = { type: "submit" } as const;

export const modifyExecutivePeopleModalContext = {
  modal: "modifyExecutivePeople",
  closeOnClickOutside: false,
  closeOnEscape: false,
  withCloseButton: false,
  innerProps: {},
};

export const ModifyExecutivePeopleModal = ({ context, id }: ContextModalProps) => {
  const { t } = useTranslation(["modals", "company", "attachments", "customer"]);
  const { t: tValidations } = useTranslation("validations");
  const { t: tHints } = useTranslation("hints");

  const contractState = useContractState();
  const { contract, uuid, getContract } = contractState;
  const isVRBProcess = contract.process === Process.VRB;
  const { czechCountryId, attachments } = useContext(EnumerationsContext);

  const validationsExecutivePerson = validationsExecutivePeople(tValidations, contract);

  const form = useForm<ExecutivePeopleFormValues>({
    initialValues: createExecutivePeopleInitialValues(contract, czechCountryId),
    transformValues: values => ({
      ...values,
      executivePeople: values.executivePeople.map(e => ({
        ...e,
        name: capitalizeLetters(e.name.trim()),
        surname: capitalizeLetters(e.surname.trim()),
      })),
    }),
    validate: {
      executivePeople: validationsExecutivePerson.executivePeople,
      uploaded: validationsExecutivePerson.uploaded,
    },
  });

  const currentPersonIndex = form.values.executivePeople.findIndex(e => e.id === form.values.currentPersonId);
  const currentExecutivePerson = form.values.executivePeople[currentPersonIndex];
  const editable = !!currentExecutivePerson?.editable;
  const showContacts = contract.process === Process.VRB || contract.segment === Segment.B2B;
  const isContractRequest = useIsContractRequest();

  const onClose = () => context.closeModal(id);
  const mutation = useUpdateMutation({
    uuid: uuid,
    form,
    onContinue: onClose,
  });

  useEffect(() => {
    sessionStorage.setItem(sessionStorageKeys.executivePeopleModal, "executivePeopleWasSet");
  }, []);

  return (
    <Box pos="relative">
      <Modal.LoadingOverlay isLoading={mutation.isPending} />
      <Modal.Title>{t("modifyExecutivePeople.title")}</Modal.Title>
      <Stack gap={24}>
        <Modal.Description
          description={
            <Text variant="reading_medium">
              {isContractRequest && contract.segment === Segment.B2B
                ? t("company:personOnBehalfOfCompanyDescriptionRequests", {
                    value: B2BEmail,
                  })
                : contract.process === Process.MOBILITY_SERVICES
                ? t("company:personOnBehalfOfCompanyDescriptionMS")
                : t("company:personOnBehalfOfCompanyDescription", {
                    value: isVRBProcess ? VRBEmail : contract.segment === Segment.B2B ? B2BEmail : InfoEmail,
                  })}
            </Text>
          }
        />
        <Form
          form={form}
          alwaysSubmit
          onContinue={() => {}}
          onSubmit={values => {
            const latestContract = getContract();
            const executivePeopleWithoutEmptyPerson =
              values.currentPersonId === "" ? values.executivePeople : values.executivePeople.filter(e => e.id !== "");

            mutation.mutate({
              ...latestContract,
              executivePeople: executivePeopleWithoutEmptyPerson.map(e => {
                const referenceType =
                  e.id === values.currentPersonId
                    ? executivePersonMainReferenceType
                    : e.referenceType === executivePersonMainReferenceType
                    ? executivePersonOtherReferenceType
                    : e.referenceType;

                if (e.id === "") {
                  return { ...DEFAULT_EXECUTIVE_PERSON, ...e, referenceType, mobileCountryCode: undefined };
                }

                const currentPerson = latestContract.executivePeople.find(({ id }) => id === e.id);

                return { ...currentPerson!, ...e, referenceType, mobileCountryCode: undefined };
              }),
            });
          }}>
          <Stack gap={24}>
            <ExecutivePersonSelect form={form} contract={contract} />
            {!!editable && (
              <>
                <Person
                  key={currentPersonIndex}
                  nameProps={form.getInputProps(`executivePeople.${currentPersonIndex}.name`)}
                  surnameProps={form.getInputProps(`executivePeople.${currentPersonIndex}.surname`)}
                  degreeBeforeIdProps={form.getInputProps(`executivePeople.${currentPersonIndex}.degreeBeforeId`)}
                  degreeAfterIdProps={form.getInputProps(`executivePeople.${currentPersonIndex}.degreeAfterId`)}
                  disabled={!editable}
                  isPrefilledCustomer={!editable}
                />
                <ExecutiveRole
                  executiveRoleProps={form.getInputProps(`executivePeople.${currentPersonIndex}.executiveRoleId`)}
                  onResetBirthDate={() => form.setFieldValue(`executivePeople.${currentPersonIndex}.birthDate`, null)}
                  onSetExecutiveRoleId={(value: string) =>
                    form.setFieldValue(`executivePeople.${currentPersonIndex}.executiveRoleId`, value)
                  }
                  disabled={!editable}
                  key={`executivePeople.${currentPersonIndex}.executiveRoleId`}
                />
              </>
            )}
            {showContacts && (
              <>
                <InputTooltip
                  showTooltip={contract.process === Process.PS}
                  tooltipProps={{
                    label: (
                      <Trans
                        i18nKey="customer.contactInfoB2B"
                        t={tHints}
                        components={{
                          eonLink: <Link href="https://login.eon.cz/prihlaseni/b2b" key="eonLink" />,
                        }}
                      />
                    ),
                  }}>
                  <Input label={t("customer:email")} {...form.getInputProps(`executivePeople.${currentPersonIndex}.email`)} pii />
                </InputTooltip>
                <MobilePhone
                  mobileCountryCodeProps={form.getInputProps(`executivePeople.${currentPersonIndex}.mobileCountryCode`)}
                  mobileProps={form.getInputProps(`executivePeople.${currentPersonIndex}.mobilePhone`)}
                  enableForeignNumbers={contract.process !== Process.VRB}
                  onSetMobilePhone={(value: string) =>
                    form.setFieldValue(`executivePeople.${currentPersonIndex}.mobilePhone`, value)
                  }
                  onSetMobileCountryCode={(value: string) =>
                    form.setFieldValue(`executivePeople.${currentPersonIndex}.mobileCountryCode`, value)
                  }
                />
              </>
            )}

            {currentExecutivePerson?.executiveRoleId !== executiveRoleId && !!editable && (
              <>
                <BirthDate
                  key={`birthdate-${currentPersonIndex}`}
                  {...form.getInputProps(`executivePeople.${currentPersonIndex}.birthDate`)}
                />
                <Text variant="reading_bold">{getAttachmentName(attachments, FileTypeCode.CAP, contract.process)}</Text>
                <Text variant="reading_medium">{t("company:attachmentsCapDescription")}</Text>
                <FileInput
                  {...form.getInputProps("uploaded")}
                  contractState={contractState}
                  fileTypeCode={FileTypeCode.CAP}
                  humanName={t("attachments:powerOfAttorneyExecutivePerson")}
                  groupId={3}
                />
              </>
            )}
            <ButtonsGroup
              withLeftButton={false}
              rightButtonText={t("modifyExecutivePeople.continue")}
              rightButtonProps={rightButtonProps}
              isLoading={mutation.isPending}
            />
          </Stack>
        </Form>
      </Stack>
    </Box>
  );
};
